import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Receipt } from "@/components/Receipt";
import { buildCreditNoteModel, buildReceiptModel, buildReturnModel } from "@/lib/receipt";
import { encodeReceipt } from "@/lib/escpos";
import { loadPrinter, sendToPrinter } from "@/lib/printers";
import { useAuth } from "@/context/AuthContext";

const PrintCtx = createContext(null);

export function PrintProvider({ children }) {
  const { session } = useAuth();
  const [job, setJob] = useState(null);

  const browserPrint = useCallback((sale) => setJob({ model: buildReceiptModel(sale, session?.business), at: Date.now() }), [session]);

  const printModel = useCallback(
    async (model) => {
      const cfg = loadPrinter();
      if (cfg.mode === "browser") return setJob({ model, at: Date.now() });
      try {
        await sendToPrinter(cfg, encodeReceipt(model, cfg.barcode_mode || "native").bytes);
        toast.success("Sent to printer");
      } catch (e) {
        toast.error("Printer not reachable", { description: "Opening browser print instead" });
        setJob({ model, at: Date.now() });
      }
    },
    []
  );
  const printSale = useCallback((sale) => printModel(buildReceiptModel(sale, session?.business)), [printModel, session]);
  const printReturn = useCallback((ret) => printModel(buildReturnModel(ret, session?.business)), [printModel, session]);
  const printKot = useCallback((k) => {
    const d = new Date(k.created_at);
    printModel({
      width: session?.business?.receipt_width || "80mm", businessName: session?.business?.name || "", branch: session?.branch?.name || "", branchAddress: "", branchPhone: "",
      header: "", taxNumber: "", title: `KOT #${k.number}`, codeLabel: k.source === "online" ? "Online" : "Table", receiptCode: k.source === "online" ? (k.order_number || "Online") : k.table_name,
      barcode: null, notes: [k.waiter_name ? `Waiter: ${k.waiter_name}` : k.customer_name ? `Customer: ${k.customer_name}` : ""].filter(Boolean),
      date: d.toLocaleDateString(), time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      items: k.items.map((i) => ({ name: `${i.void ? "VOID " : ""}${i.name}`, qtyLine: `x ${i.qty}`, amount: "", discount: null, batches: i.note ? [`Note: ${i.note}`] : [] })),
      totals: [], payment: [], footer: "",
    });
  }, [printModel, session]);
  const printCreditNote = useCallback((cn) => printModel(buildCreditNoteModel(cn, session?.business)), [printModel, session]);

  useEffect(() => {
    if (!job) return;
    const width = session?.business?.receipt_width || "80mm";
    let style = document.getElementById("receipt-page-size");
    if (!style) {
      style = document.createElement("style");
      style.id = "receipt-page-size";
      document.head.appendChild(style);
    }
    style.textContent = `@page { size: ${width} auto; margin: 0; }`;
    document.body.classList.remove("printing-tags");
    const id = requestAnimationFrame(() => window.print());
    return () => cancelAnimationFrame(id);
  }, [job, session]);

  const model = job ? job.model : null;

  return (
    <PrintCtx.Provider value={{ printSale, browserPrint, printReturn, printCreditNote, printKot }}>
      {children}
      {model && createPortal(<div id="print-root" data-testid="print-root"><Receipt model={model} /></div>, document.body)}
    </PrintCtx.Provider>
  );
}

export const usePrint = () => useContext(PrintCtx);
